import React, {CSSProperties} from 'react';
import {
  useMergeRefs,
  useTransitionStyles,
  FloatingFocusManager,
} from '@floating-ui/react';
import {
  useDevAlert,
  useIsomorphicLayoutEffect,
} from '@packages/systems/spring-hooks';

import {ModalContentView} from './ModalContent.styles';
import {useModal} from '../ModalProvider';
import {
  INITIAL_OPACITY,
  MODAL_ANIMATION_TIMING,
  MODAL_CARD_MAX_HEIGHT_VAR,
} from '../constants';

import type {HTMLAttributes, SpringTooling} from '../../__SharedTypes';

export type ModalContentProps = {
  /**
   * Provides an accessible name for the Modal.
   *
   * Please use either aria-label, aria-labelledby, or a ModalHeader with a
   * title. Pick only one. Don't combine these various methods together.
   */
  ['aria-label']?: string;
  /**
   * Provides an accessible name for the Modal.
   *
   * Please use either aria-label, aria-labelledby, or a ModalHeader with a
   * title. Pick only one. Don't combine these various methods together.
   */
  ['aria-labelledby']?: string;
  /**
   * Contents of the Modal.
   */
  children?: React.ReactNode;
  /**
   * The unique id of the Modal.
   */
  id?: string;
  /**
   * Centers the Modal on the screen. By default, `isCentered` is disabled, and the
   * `Modal` is positioned closer to the top. Use with `shouldAnchorModalTop` to prevent the Modal from moving if its content grows.
   */
  isCentered?: boolean;
  /**
   * Stretches the Modal to the top and bottom of the screen.  Combine this with
   * Modal sizing to achieve a better visual effect.
   */
  isFullHeight?: boolean;
  /**
   * Stretches the Modal to the full height and width of the screen.
   *
   * Will override other `isCentered` and `isFullHeight` props.
   */
  isFullScreen?: boolean;
  /**
   * Use to set the Modal's margin value. `_margin` will be overridden by `isCentered` or `isFullHeight` if applied.
   *
   * @default '5vh auto'
   */
  _margin?: CSSProperties['margin'];
  /**
   * Determines if the Modal should animate on open and close.
   * @ignore
   */
  shouldDisableAnimations?: boolean;
  /**
   * Determines if the document.body should scroll when the
   * modal is opened.
   */
  shouldPreventBodyScroll?: boolean;
  /**
   * Determines if the Modal should bounce in with a "pop". By default, the bounce animation is enabled.
   * If this prop is set to `false`, the Modal will animate in with a fade. Animations are completely
   * disabled if `shouldDisableAnimations` is set to `true`.
   */
  shouldUseBounceAnimation?: boolean;
} & HTMLAttributes &
  SpringTooling;

export const ModalContent = React.forwardRef<HTMLDivElement, ModalContentProps>(
  (
    {
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledby,
      children,
      isCentered = false,
      isFullHeight,
      isFullScreen,
      _margin,
      shouldDisableAnimations,
      shouldUseBounceAnimation,
      ...props
    },
    propRef
  ) => {
    const {
      modalTitleId,
      modalDescriptionId,
      isNonModalDialog,
      hasCloseButton,
      onOpenFocusRef,
      dialog,
      shouldAnchorModalTop,
    } = useModal();
    useDevAlert(
      Boolean(ariaLabel) && Boolean(modalTitleId),
      'Provide either aria-label or aria-labelledby, not both.'
    );

    const [anchoredTop, setAnchoredTop] = React.useState({});
    const {refs, getFloatingProps, context} = dialog;
    const contentRef = React.useRef<HTMLDivElement>(null);

    const mergedRefs = useMergeRefs([refs.setFloating, propRef, contentRef]);

    useIsomorphicLayoutEffect(() => {
      if (isCentered && shouldAnchorModalTop && contentRef.current) {
        /**
         * We want to center the modal card on the screen.
         *
         * We are using the middle of the card as our anchor point, rather than the top of the modal itself.
         * This will visually center the card on the screen.
         * However, we need to make sure that the top of the card is never less than 5vh from the top of the screen.
         *
         * We are also setting the max height of the card to ensure that its content will not overflow the screen.
         * There will always be 5vh bottom margins.
         */
        const contentHeight = Math.ceil(
          contentRef.current.getBoundingClientRect().height
        );

        /**
         * The goal here is to set the margin top to the desired value, but not exceed 5vh.
         * We are assigning the desired margin top to a local variable because react is stripping out the `calc()` if we inline it for some reason.
         */
        const localMarginTop = `calc(50vh - ${contentHeight / 2}px)`;
        const marginTop = `max(5vh, var(--local-margin-top))`;

        /**
         * We are assigning the desired max height of our card here so that the `ModalCard` css can grab it through the CSS cascade and use it if present.
         * This is much easier and more performant than trying to coordinate this value through the modal context.
         *
         * The idea here is that we always want at least 5vh of space below the card.
         * Because we are shifting the card up by contentHeight/2, then we need to add that amount back in.
         */
        const maxCardHeight = `calc(45vh + ${contentHeight / 2}px)`;
        setAnchoredTop({
          [MODAL_CARD_MAX_HEIGHT_VAR]: maxCardHeight,
          '--local-margin-top': localMarginTop,
          marginTop,
        });
      } else {
        setAnchoredTop({});
      }
    }, [isCentered, shouldAnchorModalTop, mergedRefs]);

    const {styles: transitionStyles} = useTransitionStyles(context, {
      duration: shouldDisableAnimations ? 0 : MODAL_ANIMATION_TIMING,
      initial: {
        opacity: shouldDisableAnimations ? 1 : INITIAL_OPACITY,
        transform:
          !shouldDisableAnimations && shouldUseBounceAnimation
            ? 'scale(0.96)'
            : undefined,
      },
      open: {
        opacity: 1,
        transform: 'scale(1)',
      },
    });

    const styles = {
      ...anchoredTop,
      ...transitionStyles,
    };

    return (
      <FloatingFocusManager
        context={dialog.context}
        disabled={isNonModalDialog && !isFullScreen}
        initialFocus={onOpenFocusRef ?? dialog.refs.floating}
        modal={!isNonModalDialog || isFullScreen}
        visuallyHiddenDismiss={!hasCloseButton}
      >
        <ModalContentView
          applyDefaultPositioning={
            !_margin && !isCentered && !(isFullHeight || isFullScreen)
          }
          aria-describedby={modalDescriptionId}
          aria-label={ariaLabel}
          aria-labelledby={modalTitleId ?? ariaLabelledby}
          isCentered={isCentered}
          isFullHeight={isFullHeight || isFullScreen}
          ref={mergedRefs}
          style={{
            pointerEvents: 'auto', // reset pointer events so that nonModalDialog can be interacted with
            ...styles,
          }}
          tabIndex={0}
          {...getFloatingProps(props)}
          aria-modal={isNonModalDialog && !isFullScreen ? undefined : true}
          margin={
            isCentered || isFullHeight || isFullScreen ? undefined : _margin
          }
        >
          {children}
        </ModalContentView>
      </FloatingFocusManager>
    );
  }
);

ModalContent.displayName = 'ModalContent';
